import React, { useState, useRef } from "react";
import { BASE_URL } from "utils/constant";

const DeliveryPaymentFlow = ({ onClose, delivery }) => {
  const [currentStep, setCurrentStep] = useState("delivery");
  const [deliveryData, setDeliveryData] = useState(delivery);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleNext = () => {
    setCurrentStep("payment");
  };

  const handleBack = () => {
    setCurrentStep("delivery");
  };

  return (
    <div className="relative w-full max-w-2xl mx-auto bg-white rounded-xl shadow-2xl flex flex-col h-[80vh] max-h-[800px]">
      <div className="h-full flex flex-col overflow-hidden rounded-xl">
        <div className="flex-1 overflow-hidden relative">
          <div
            className="flex h-full transition-transform duration-300 ease-in-out"
            style={{
              transform:
                currentStep === "payment" ? "translateX(-100%)" : "translateX(0)",
            }}
          >
            <div className="w-full flex-shrink-0 h-full">
              <MaterialDeliveryCheck
                onComplete={handleNext}
                onClose={onClose}
                deliveryData={deliveryData}
                setDeliveryData={setDeliveryData}
              />
            </div>
            <div className="w-full flex-shrink-0 h-full">
              <PaymentDetails
                onClose={onClose}
                onBack={handleBack}
                deliveryData={deliveryData}
                setDeliveryData={setDeliveryData}
                isSubmitting={isSubmitting}
                setIsSubmitting={setIsSubmitting}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const CheckSection = ({ title, rightContent, children }) => (
  <div className="py-2 border-b border-gray-100 last:border-0">
    <div className="flex justify-between items-center mb-1">
      <span className="text-gray-700 text-sm font-semibold">{title}</span>
      {rightContent && (
        <div className="text-right">
          <div className="font-bold text-sm text-gray-900">{rightContent.main}</div>
        </div>
      )}
    </div>
    <div className="pt-1">
      {children}
    </div>
  </div>
);

const RadioOption = ({ label, name, value, checked, onChange }) => (
  <label className="flex items-center cursor-pointer group select-none">
    <div className={`w-4 h-4 rounded-full border flex items-center justify-center mr-2 transition-colors ${checked ? 'border-purple-600 bg-purple-50' : 'border-gray-300 bg-white group-hover:border-purple-400'}`}>
      {checked && <div className="w-2 h-2 rounded-full bg-purple-600" />}
    </div>
    <input
      type="radio"
      name={name}
      value={value}
      checked={checked}
      onChange={onChange}
      className="hidden"
    />
    <span className={`text-sm ${checked ? 'text-purple-700 font-medium' : 'text-gray-500'}`}>{label}</span>
  </label>
);

const MaterialDeliveryCheck = ({
  onComplete,
  onClose,
  deliveryData,
  setDeliveryData,
}) => {
  const updateTimersRef = useRef({});

  const updateMaterialCheck = (materialId, checkData) => {
    // Optimistically update UI immediately for smooth typing/backspace
    setDeliveryData((prev) => ({
      ...prev,
      all_materials: prev.all_materials.map((material) =>
        material.id === materialId ? { ...material, ...checkData } : material
      ),
    }));

    // Debounce API calls per material to avoid spamming on every keystroke
    if (updateTimersRef.current[materialId]) {
      clearTimeout(updateTimersRef.current[materialId]);
    }

    updateTimersRef.current[materialId] = setTimeout(async () => {
      try {
        const accessToken = localStorage.getItem("access");

        // Sanitize payload (allow empty string -> null; ensure numbers are numbers)
        const payload = { ...checkData };
        if (Object.prototype.hasOwnProperty.call(payload, "false_quantity")) {
          const val = payload.false_quantity;
          payload.false_quantity =
            val === "" || val === null ? null : Number(val);
        }

        const response = await fetch(
          `${BASE_URL}/inventory/update-materials-of-delivery/${materialId}/`,
          {
            method: "PATCH",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${accessToken}`,
            },
            body: JSON.stringify(payload),
          }
        );

        if (!response.ok) {
          console.error("Failed to update material check");
        }
      } catch (error) {
        console.error("Error updating material check:", error);
      } finally {
        delete updateTimersRef.current[materialId];
      }
    }, 300);
  };

  return (
    <div className="h-full flex flex-col bg-white">
      {/* Header */}
      <div className="flex-shrink-0 px-6 py-4 border-b border-gray-100 bg-white z-10">
        <div className="relative flex items-center justify-center mb-4">
          <h1 className="text-xl font-bold text-gray-800">Delivery Check</h1>
          <button 
            className="absolute right-0 p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors" 
            onClick={onClose}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        
        <div className="flex items-center gap-2 text-sm">
            <span className="font-medium text-purple-600">Step 1</span>
            <span className="text-gray-300">/</span>
            <span className="text-gray-500">Step 2</span>
        </div>
        <div className="w-full bg-gray-100 h-1.5 rounded-full mt-2 overflow-hidden">
          <div className="w-1/2 bg-purple-600 h-full rounded-full transition-all duration-300" />
        </div>
      </div>

      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto p-4 bg-gray-50/50">
        <div className="space-y-4">
          {deliveryData.all_materials.map((material, index) => (
            <div key={material.id} className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm hover:shadow-md transition-shadow">
              <div className="flex items-start justify-between mb-2 pb-2 border-b border-gray-100">
                <div>
                    <h3 className="text-base font-bold text-gray-800">{material.material_name}</h3>
                    <p className="text-xs text-gray-500 mt-0.5">{material.material_subcategory}</p>
                </div>
                <div className="bg-purple-50 text-purple-700 text-xs font-medium px-2.5 py-1 rounded-full">
                    #{index + 1}
                </div>
              </div>

              <div className="flex flex-col">
                <CheckSection
                  title="Specification"
                  rightContent={{
                    main: `${material.unit_price}/${material.unit}`,
                    sub: "Price",
                  }}
                >
                  <div className="flex gap-4 mb-1">
                      <RadioOption
                        label="Matched"
                        name={`spec-${material.id}`}
                        value="Matched"
                        checked={material.specification_check === "Matched"}
                        onChange={() =>
                          updateMaterialCheck(material.id, {
                            specification_check: "Matched",
                            specification_check_comment: "",
                          })
                        }
                      />
                      <RadioOption
                        label="Fault"
                        name={`spec-${material.id}`}
                        value="Fault"
                        checked={material.specification_check === "Fault"}
                        onChange={() =>
                          updateMaterialCheck(material.id, {
                            specification_check: "Fault",
                          })
                        }
                      />
                  </div>
                    {material.specification_check === "Fault" && (
                      <div className="animate-fadeIn mt-2">
                        <input
                            type="text"
                            placeholder="Specify fault details..."
                            value={material.specification_check_comment || ""}
                            onChange={(e) =>
                            updateMaterialCheck(material.id, {
                                specification_check: "Fault",
                                specification_check_comment: e.target.value,
                            })
                            }
                            className="w-full p-2 text-sm border border-gray-200 rounded-md bg-gray-50 focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500 transition-all"
                            autoFocus
                        />
                      </div>
                    )}
                </CheckSection>

                <CheckSection
                  title="Quantity"
                  rightContent={{
                    main: `${material.quantity} ${material.unit}`,
                    sub: "Ordered",
                  }}
                >
                  <div className="flex gap-4 mb-1">
                    <RadioOption
                        label="Matched"
                        name={`quantity-${material.id}`}
                        value="Matched"
                        checked={material.quantity_check === "Matched"}
                        onChange={() =>
                        updateMaterialCheck(material.id, {
                            quantity_check: "Matched",
                            false_quantity: null,
                        })
                        }
                    />
                    <RadioOption
                        label="Fault"
                        name={`quantity-${material.id}`}
                        value="Fault"
                        checked={material.quantity_check === "Fault"}
                        onChange={() =>
                        updateMaterialCheck(material.id, {
                            quantity_check: "Fault",
                        })
                        }
                    />
                  </div>
                    {material.quantity_check === "Fault" && (
                      <div className="mt-2 relative animate-fadeIn">
                           <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs z-10 pointer-events-none">
                              
                           </span>
                          <input
                            type="number"
                            placeholder="0"
                            value={material.false_quantity || ""}
                            onChange={(e) =>
                              updateMaterialCheck(material.id, {
                                quantity_check: "Fault",
                                false_quantity: e.target.value,
                              })
                            }
                            className="w-full pl-20 p-2 text-sm border border-gray-200 rounded-md bg-gray-50 focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500 transition-all"
                            autoFocus
                          />
                      </div>
                    )}
                </CheckSection>

                <CheckSection title="Quality">
                  <div className="flex gap-4">
                      <RadioOption
                        label="Matched"
                        name={`quality-${material.id}`}
                        value="Matched"
                        checked={material.quality_check === "Matched"}
                        onChange={() =>
                          updateMaterialCheck(material.id, {
                            quality_check: "Matched",
                            false_quantity: null,
                          })
                        }
                      />
                      <RadioOption
                        label="Fault"
                        name={`quality-${material.id}`}
                        value="Fault"
                        checked={material.quality_check === "Fault"}
                        onChange={() =>
                          updateMaterialCheck(material.id, {
                            quality_check: "Fault",
                          })
                        }
                      />
                  </div>
                </CheckSection>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Footer */}
      <div className="flex-shrink-0 p-4 border-t border-gray-200 bg-white flex justify-center">
        <button
          onClick={onComplete}
          className="w-auto px-12 bg-purple-600 hover:bg-purple-700 text-white py-3 rounded-xl flex items-center justify-center font-semibold transition-all shadow-lg shadow-purple-200 hover:shadow-purple-300"
        >
          Continue to Payment
          <svg
            className="w-5 h-5 ml-2"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M17 8l4 4m0 0l-4 4m4-4H3"
            />
          </svg>
        </button>
      </div>
    </div>
  );
};

const PaymentDetails = ({
  onClose,
  onBack,
  deliveryData,
  setDeliveryData,
  isSubmitting,
  setIsSubmitting,
}) => {
  const [newAmount, setNewAmount] = useState("");
  const [paymentMethod, setPaymentMethod] = useState(
    deliveryData.payment_method
  );
  const [error, setError] = useState("");

  const paymentMethods = ["Cash", "UPI/Bank Transfer", "Cheque"];

  const handleMarkAsReceived = async () => {
    try {
      setIsSubmitting(true);
      setError("");

      const accessToken = localStorage.getItem("access");

      // If there's a new payment amount, create part payment first
      if (newAmount && Number(newAmount) > 0) {
        const partPaymentResponse = await fetch(
          `${BASE_URL}/inventory/create-partpayment/`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${accessToken}`,
            },
            body: JSON.stringify({
              paid_amount: Number(newAmount),
              payment_method: paymentMethod,
              delivery: deliveryData.id,
            }),
          }
        );

        if (!partPaymentResponse.ok) {
          const errorData = await partPaymentResponse.json();
          throw new Error(errorData.message || "Failed to create part payment");
        }
      }

      // Then mark as delivered
      const response = await fetch(`${BASE_URL}/inventory/mark-as-delivered/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          delivery_id: deliveryData.id,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to mark as received");
      }

      // Close the modal after successful submission
      onClose();
    } catch (error) {
      setError(error.message || "Failed to process request");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReturn = async () => {
    try {
      setIsSubmitting(true);
      setError("");

      const accessToken = localStorage.getItem("access");

      const response = await fetch(
        `${BASE_URL}/inventory/update-delivery/${deliveryData.id}/`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify({
            transaction_id: 3,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to process return");
      }

      // Close the modal after successful submission
      onClose();
    } catch (error) {
      setError(error.message || "Failed to process return");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="h-full flex flex-col bg-white">
      {/* Header */}
      <div className="flex-shrink-0 px-6 py-4 border-b border-gray-100 bg-white z-10">
        <div className="relative flex items-center justify-center mb-4">
          <button 
            className="absolute left-0 p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-full transition-colors" 
            onClick={onBack}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
          </button>
          <h1 className="text-xl font-bold text-gray-800">Payment Details</h1>
        </div>

        <div className="flex items-center gap-2 text-sm">
            <span className="text-gray-400">Step 1</span>
            <span className="text-gray-300">/</span>
            <span className="font-medium text-purple-600">Step 2</span>
        </div>
        <div className="w-full bg-gray-100 h-1.5 rounded-full mt-2 overflow-hidden">
          <div className="w-full bg-purple-600 h-full rounded-full transition-all duration-300" />
        </div>

        {error && (
          <div className="mt-3 p-3 bg-red-50 text-red-700 rounded-lg text-sm border border-red-100 flex items-center">
            <svg className="w-4 h-4 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            {error}
          </div>
        )}
      </div>

      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto p-4 bg-gray-50/50">
        <div className="flex flex-col gap-3">
          {/* Payment Summary */}
          <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
            <h3 className="text-sm font-bold mb-3 text-gray-800 flex items-center">
                <svg className="w-5 h-5 mr-2 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
                Summary
            </h3>
            <div className="space-y-2">
              <div className="flex justify-between items-center text-sm">
                <span className="text-gray-500">Total Amount</span>
                <span className="font-bold text-gray-900">₹ {deliveryData.total_price}</span>
              </div>
              <div className="flex justify-between items-center text-sm">
                <span className="text-gray-500">Paid Amount</span>
                <span className="font-bold text-green-600">₹ {deliveryData.paid_amount}</span>
              </div>
              <div className="h-px bg-gray-100 my-1"></div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600 font-medium">Payment Due</span>
                <span className="font-bold text-base text-red-600">₹ {deliveryData.remaining_payment}</span>
              </div>
            </div>
          </div>

          {/* Payment Form */}
          <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
            <h3 className="text-sm font-bold mb-3 text-gray-800 flex items-center">
                <svg className="w-5 h-5 mr-2 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
                Add Payment
            </h3>
            <div className="space-y-4 max-w-md">
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                  Amount
                </label>
                <div className="relative w-60">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 font-bold">
                    ₹
                  </span>
                  <input
                    type="number"
                    className="w-full py-1.5 pl-8 pr-3 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500 transition-all text-gray-900 placeholder-gray-400 text-sm"
                    placeholder="Enter amount"
                    value={newAmount}
                    onChange={(e) => setNewAmount(e.target.value)}
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Method</label>
                <div className="grid grid-cols-3 gap-2">
                  {paymentMethods.map((method) => (
                    <button
                      key={method}
                      className={`px-2 py-1.5 rounded-lg text-[11px] font-medium transition-all border ${
                        paymentMethod === method
                          ? "bg-purple-50 border-purple-200 text-purple-700 shadow-sm"
                          : "bg-white border-gray-200 text-gray-600 hover:bg-gray-50 hover:border-gray-300"
                      }`}
                      onClick={() => setPaymentMethod(method)}
                    >
                      {method}
                    </button>
                  ))}
                </div>
              </div>

 
                 <div className="flex items-center text-sm gap-2">
                  <span className="text-gray-500">Remaining Balance</span>
                  <span className="font-bold text-gray-900">
                    ₹ {Math.max(0, deliveryData.remaining_payment - (Number(newAmount) || 0))}
                  </span>
                </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="flex-shrink-0 p-4 border-t border-gray-200 bg-white">
        <div className="flex gap-3 justify-center">
          <button
            className={`w-40 bg-green-600 hover:bg-green-700 text-white py-3 rounded-xl flex items-center justify-center text-sm font-semibold transition-all shadow-lg shadow-green-200 hover:shadow-green-300 ${
              isSubmitting ? "opacity-50 cursor-not-allowed" : ""
            }`}
            onClick={handleMarkAsReceived}
            disabled={isSubmitting}
          >
            {isSubmitting ? "Processing..." : "Mark Received"}
            {!isSubmitting && (
              <svg
                className="w-4 h-4 ml-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            )}
          </button>
          <button
            className={`w-40 bg-white border-2 border-red-100 text-red-600 hover:bg-red-50 py-3 rounded-xl flex items-center justify-center text-sm font-semibold transition-all ${
              isSubmitting ? "opacity-50 cursor-not-allowed" : ""
            }`}
            onClick={handleReturn}
            disabled={isSubmitting}
          >
            Return Items
          </button>
        </div>
      </div>
    </div>
  );
};

export default DeliveryPaymentFlow;
